import {
  Box,
  useTheme,
  TextField,
  Divider,
  MenuItem,
  Checkbox,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";

import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useAuth } from "../../../contexts/AuthContext";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import dayjs from "dayjs";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";

const splitLegacyAddress = (value) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) {
    return { line1: "", line2: "", line3: "" };
  }

  const newlineParts = normalized
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (newlineParts.length >= 3) {
    return {
      line1: newlineParts[0],
      line2: newlineParts.slice(1, -1).join(", "),
      line3: newlineParts[newlineParts.length - 1],
    };
  }

  if (newlineParts.length === 2) {
    return {
      line1: newlineParts[0],
      line2: "",
      line3: newlineParts[1],
    };
  }

  const commaParts = normalized
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (commaParts.length >= 3) {
    return {
      line1: commaParts[0],
      line2: commaParts.slice(1, -1).join(", "),
      line3: commaParts[commaParts.length - 1],
    };
  }

  if (commaParts.length === 2) {
    return {
      line1: commaParts[0],
      line2: "",
      line3: commaParts[1],
    };
  }

  return { line1: normalized, line2: "", line3: "" };
};

const normalizeAddressPart = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized || normalized.toUpperCase() === "N/A") return null;
  return normalized;
};

const ClientNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  const { overlayData, setOverlayName, setOverlayData, triggerPageRefresh } =
    useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW
      ? "New Client"
      : "Edit Client",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const isNewClient = overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW;

  // Edit lock for existing clients only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "client",
    isNewClient ? null : overlayData.Client?.id,
  );

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (overlayData.ClientID) {
      setCurrentEntity({
        entityType: "Client",
        entityId: overlayData.ClientID,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [overlayData.ClientID, setCurrentEntity]);

  // Listen for client updates from other members
  const handleClientRefetch = useCallback(() => {
    // Reload the overlay data with fresh data from server
    api
      .get(`/clients/${overlayData.Client?.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          Client: response.data,
        });
        triggerPageRefresh("Client data refreshed", theme);
      })
      .catch((err) => console.log("Error refetching client:", err));
  }, [overlayData, setOverlayData, triggerPageRefresh, theme]);

  useAssignmentUpdates({
    modelType: "Client",
    recordId: isNewClient ? null : overlayData.Client?.id,
    onUpdate: handleClientRefetch,
  });

  const [accountError, setAccountError] = useState(false);
  const [shortNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);

  const [account, setAccount] = useState(
    isNewClient ? "" : overlayData.Client.Account,
  );

  const [shortName, setShortName] = useState(
    isNewClient ? "" : overlayData.Client.ShortName || "N/A",
  );

  const legacyAddress = splitLegacyAddress(overlayData.Client?.MainAddress);
  const hasStructuredAddress = isNewClient
    ? false
    : [
        overlayData.Client?.AddressLine1,
        overlayData.Client?.AddressLine2,
        overlayData.Client?.AddressLine3,
      ].some((part) => typeof part === "string" && part.trim() !== "");

  const initialAddress = isNewClient
    ? { line1: "", line2: "", line3: "" }
    : hasStructuredAddress
      ? {
          line1: normalizeAddressPart(overlayData.Client.AddressLine1) || "",
          line2: normalizeAddressPart(overlayData.Client.AddressLine2) || "",
          line3: normalizeAddressPart(overlayData.Client.AddressLine3) || "",
        }
      : {
          line1: normalizeAddressPart(legacyAddress.line1) || "",
          line2: normalizeAddressPart(legacyAddress.line2) || "",
          line3: normalizeAddressPart(legacyAddress.line3) || "",
        };

  const [addressLine1, setAddressLine1] = useState(initialAddress.line1);
  const [addressLine2, setAddressLine2] = useState(initialAddress.line2);
  const [addressLine3, setAddressLine3] = useState(initialAddress.line3);

  const [type, setType] = useState(isNewClient ? "" : overlayData.Client.Type);
  const [contractStatus, setContractStatus] = useState(
    isNewClient
      ? ""
      : overlayData.Client.ClientContractHistories &&
          overlayData.Client.ClientContractHistories.length > 0
        ? overlayData.Client.ClientContractHistories[0].Status
        : "",
  );

  const [minimumOPIRate, setMinimumOPIRate] = useState(
    overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW
      ? 0
      : overlayData.Client.OPIMinimumRate,
  );

  const [invoiceNoteTemplate, setInvoiceNoteTemplate] = useState(
    overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW
      ? ""
      : overlayData.Client.InvoiceNoteTemplate || "",
  );

  const addNewClient = useCallback((data) => {
    api
      .post("/clients/addNewClient", {
        data: data,
      })
      .then((response) => {
        //Add the default rates of the client

        setIsSavingChanges(false);

        triggerPageRefresh("New Client Added Successfully!", theme);

        // Emit client saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Client",
            recordId: response.data.id || response.data.ClientID,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });

          // Also notify the clients list page to refetch
          socket.emit("assignment:saved", {
            modelType: "ClientsList",
            recordId: "all",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [currentUser, setOverlayName, socket, theme, triggerPageRefresh]);

  const updateClient = useCallback((data) => {
    api
      .post("/clients/updateClient", {
        data: data,
      })
      .then((response) => {
        setIsSavingChanges(false);

        triggerPageRefresh("client Updated Sucessfully", theme);

        // Emit client saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Client",
            recordId: overlayData.ClientID,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });

          // Also notify the clients list page to refetch
          socket.emit("assignment:saved", {
            modelType: "ClientsList",
            recordId: "all",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [currentUser, overlayData.ClientID, setOverlayName, socket, theme, triggerPageRefresh]);

  const previousStatus = overlayData.Client
    ? overlayData.Client.ClientContractHistories !== null &&
      overlayData.Client.ClientContractHistories.length > 0
      ? overlayData.Client.ClientContractHistories[0].Status
      : ""
    : "";

  const [checkedServiceTypes, setCheckedServiceTypes] = useState(
    overlayData.Client
      ? overlayData.Client.ClientServiceTypes.map((item) => item.ServiceType.id)
      : [],
  );

  const [checkedPaymentMethods, setCheckedPaymentMethods] = useState(
    overlayData.Client
      ? (overlayData.Client.ClientPaymentMethods || []).map(
          (item) => item.PaymentMethodID || item.PaymentMethod?.id,
        )
      : [],
  );

  const [paymentMethodNotesById, setPaymentMethodNotesById] = useState(() => {
    if (!overlayData.Client?.ClientPaymentMethods?.length) {
      return {};
    }

    return overlayData.Client.ClientPaymentMethods.reduce((acc, item) => {
      const methodId = item.PaymentMethodID || item.PaymentMethod?.id;

      if (methodId) {
        acc[methodId] = item.DefaultInvoiceNote || "";
      }

      return acc;
    }, {});
  });

  const saveChanges = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    const today = dayjs.tz(undefined, "America/New_York");
    const month = today.month() + 1;
    const year = today.year();
    const nextYear = year + 1;
    const day = today.date();

    const showTime = today.format("HH:mm:ss");

    const normalizedAddressLine1 = normalizeAddressPart(addressLine1);
    const normalizedAddressLine2 = normalizeAddressPart(addressLine2);
    const normalizedAddressLine3 = normalizeAddressPart(addressLine3);

    const data = {
      ClientID: overlayData.ClientID,
      Client: {
        Account: account,
        ShortName: shortName || "",
        MainAddress: [
          normalizedAddressLine1,
          normalizedAddressLine2,
          normalizedAddressLine3,
        ]
          .filter(Boolean)
          .join(", "),
        AddressLine1: normalizedAddressLine1,
        AddressLine2: normalizedAddressLine2,
        AddressLine3: normalizedAddressLine3,
        Type: type || null,
        InvoiceNoteTemplate: invoiceNoteTemplate || null,
        OPIMinimumRate: minimumOPIRate || null,
      },
      ClientServiceTypesIDs: checkedServiceTypes,
      ClientContractHistory: {
        Status: contractStatus,
        PreviousStatus: previousStatus,
        Time: `${showTime}`,
        Date: `${year}-${month}-${day}`,
        RenewDate: `${nextYear}-${month}-${day}`,
      },
      MemberID: currentUser.id,
      ClientPaymentMethodIDs: checkedPaymentMethods,
      ClientPaymentMethodNotes: checkedPaymentMethods.reduce((acc, methodId) => {
        const note = paymentMethodNotesById[methodId];
        acc[methodId] =
          typeof note === "string" && note.trim().length > 0 ? note.trim() : null;
        return acc;
      }, {}),
    };

    if (overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW) {
      addNewClient(data);
      return;
    }

    updateClient(data);
  }, [
    overlayData,
    account,
    shortName,
    addressLine1,
    addressLine2,
    addressLine3,
    type,
    invoiceNoteTemplate,
    minimumOPIRate,
    checkedServiceTypes,
    checkedPaymentMethods,
    paymentMethodNotesById,
    contractStatus,
    previousStatus,
    currentUser.id,
    addNewClient,
    updateClient,
  ]);

  const handleServiceTypesToggle = (value) => () => {
    const currentIndex = checkedServiceTypes.indexOf(value);
    const newChecked = [...checkedServiceTypes];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    if (value === 2) setMinimumOPIRate(0);

    setCheckedServiceTypes(newChecked);
  };

  const handlePaymentMethodsToggle = (value) => () => {
    const currentIndex = checkedPaymentMethods.indexOf(value);
    const newChecked = [...checkedPaymentMethods];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedPaymentMethods(newChecked);
  };

  const handlePaymentMethodNoteChange = useCallback((methodId, value) => {
    setPaymentMethodNotesById((prev) => ({
      ...prev,
      [methodId]: value,
    }));
  }, []);

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked) {
      return;
    }

    saveChanges();
  }, [isLocked, isSavingChanges, saveChanges]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          //minWidth: { xs: "100%", sm: "600px" },
          height: "fit-content",
          maxHeight: { xs: "95%", sm: "90%" },
          //maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              //width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
              //minWidth: { xs: "100%", sm: "600px" },
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW
                ? "New Client"
                : "Edit Client"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "40%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}

            <Box p="0 .5rem" />

            {isMobile ? (
              <Tooltip title="Save" placement="top">
                <IconButton
                  color="primary"
                  onClick={attemptSave}
                  disabled={isSavingChanges || isLocked}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    borderRadius: "12px",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  <SaveRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                loading={isSavingChanges}
                disabled={isSavingChanges || isLocked}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={attemptSave}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            //flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            maxHeight: "60vh",
            overflowY: "auto",
            width: "100%",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Account Information
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Account"
                label="Account"
                error={accountError}
                value={account}
                onChange={(event) => {
                  setAccount(event.target.value);
                  setAccountError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="ShortName"
                label="Short Name"
                error={shortNameError}
                value={shortName}
                onChange={(event) => {
                  setShortName(event.target.value);
                  //setShortNameError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine1"
                label="Address Line 1"
                error={addressError}
                value={addressLine1}
                onChange={(event) => {
                  setAddressLine1(event.target.value);
                  setAddressError(false);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine2"
                label="Address Line 2 (Optional)"
                value={addressLine2}
                onChange={(event) => {
                  setAddressLine2(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine3"
                label="City, State ZIP"
                value={addressLine3}
                onChange={(event) => {
                  setAddressLine3(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="Type"
                label="Type"
                value={type}
                onChange={(event) => {
                  setType(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                {overlayData.ClientTypes.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="ContractStatus"
                label="Contract Status"
                value={contractStatus}
                onChange={(event) => {
                  setContractStatus(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                {overlayData.ContractStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable || !checkedServiceTypes.includes(2)}
                size="small"
                id="MinimumOPIRate"
                label="Minimum OPI Rate"
                value={minimumOPIRate}
                //error={minimumOPIRateError}
                type="number"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                onChange={(event) => {
                  setMinimumOPIRate(event.target.value);
                  //setMinimumOPIRateError(typeof event.target.value !== "number");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                multiline
                id="InvoiceNoteTemplate"
                label="Invoice Note Template"
                value={invoiceNoteTemplate}
                onChange={(event) => {
                  setInvoiceNoteTemplate(event.target.value);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Service Types
              </Typography>
            </Grid>

            {overlayData.ClientServiceTypes.map((clientServiceType) => {
              const labelId = `checkbox-list-label-${clientServiceType.id}`;

              return (
                <Grid
                  key={clientServiceType.id}
                  size={{ xs: 12, sm: 12, md: 4, lg: 2.4 }}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      role={undefined}
                      onClick={handleServiceTypesToggle(clientServiceType.id)}
                      dense
                      disabled={!isEditable}
                    >
                      <ListItemIcon>
                        <Checkbox
                          disabled={!isEditable}
                          edge="start"
                          checked={checkedServiceTypes.includes(
                            clientServiceType.id,
                          )}
                          tabIndex={-1}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        disableTypography
                        primary={
                          <Typography variant="body1">
                            {clientServiceType.Name}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </Grid>
              );
            })}

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Payment Methods
              </Typography>
            </Grid>

            {(overlayData.PaymentMethods || []).map((paymentMethod) => {
              const labelId = `checkbox-list-label-payment-method-${paymentMethod.id}`;
              const isChecked = checkedPaymentMethods.includes(paymentMethod.id);

              return (
                <Grid
                  key={paymentMethod.id}
                  size={{ xs: 12, sm: 12, md: 4, lg: 2.4 }}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      role={undefined}
                      onClick={handlePaymentMethodsToggle(paymentMethod.id)}
                      dense
                      disabled={!isEditable}
                    >
                      <ListItemIcon>
                        <Checkbox
                          disabled={!isEditable}
                          edge="start"
                          checked={isChecked}
                          tabIndex={-1}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        disableTypography
                        primary={
                          <Typography variant="body1">
                            {paymentMethod.Name}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {isChecked && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      disabled={!isEditable}
                      label="Default Invoice Note"
                      value={paymentMethodNotesById[paymentMethod.id] || ""}
                      onChange={(event) =>
                        handlePaymentMethodNoteChange(
                          paymentMethod.id,
                          event.target.value,
                        )
                      }
                      placeholder={`Optional default note for ${paymentMethod.Name} invoices`}
                      sx={{ mt: 1 }}
                    />
                  )}

                </Grid>
              );
            })}

            {/*overlayData.ClientServiceTypes.map((contractStatus, index) => (
              <Grid size={{ xs: 12, md: 2.4 }} container spacing={2}>
                <FormControlLabel
                  value={contractStatus.Name}
                  control={
                    <Checkbox
                      disabled={!isEditable}
                      label={contractStatus.Name}
                      id={contractStatus.Name}
                      checked={
                        overlayData.Action === OVERLAY_NAMES.CLIENT_ACTION_NEW
                          ? false
                          : overlayData.Client.ClientServiceTypes &&
                            overlayData.Client.ClientServiceTypes.some(
                              (item) =>
                                item.ServiceType.Name === contractStatus.Name
                            )
                      }
                    />
                  }
                  label={contractStatus.Name}
                />
              </Grid>
            ))*/}
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default ClientNewEdit;
